import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  PAGE_SIZE,
  activateTeam,
  isAbortError,
  loadCore,
  loadGroupPage,
  peekCore,
  searchGroups,
} from 'shared/utils/filters/propertyCatalogStore';

/** Pause after the last keystroke before the server is asked. */
export const SEARCH_DEBOUNCE_MS = 250;

const emptyPage = () => ({ items: [], hasMore: true, loaded: false, loading: false, offset: 0 });

/**
 * Lazy property catalog for a filter picker.
 *
 *   core      the fixed properties: static at once, replaced by the server's
 *             copy on first open, cached per team after that.
 *   pages     per deferred group (`groups`), loaded `PAGE_SIZE` at a time by
 *             `loadMore(group)` / `loadNext()` — never all at once.
 *   search    `setQuery(q)`: after SEARCH_DEBOUNCE_MS, asks the server for
 *             matching names across the deferred groups, but only when some
 *             group is not fully loaded (otherwise the loaded items already
 *             hold every possible match). A newer query aborts the older one.
 *
 * State is per (team, agent): changing either resets it, and the store drops
 * the previous team's cache.
 */
export default function usePropertyCatalog({ open, teamId, agentName = null, groups }) {
  const groupKey = (groups || []).join(',');
  const groupIds = useMemo(() => (groupKey ? groupKey.split(',') : []), [groupKey]);
  const scopeKey = `${teamId ?? ''}\u0001${agentName ?? ''}\u0001${groupKey}`;

  const [core, setCore] = useState(() => peekCore(teamId));
  const [pages, setPages] = useState(() => Object.fromEntries(groupIds.map((g) => [g, emptyPage()])));
  const [search, setSearch] = useState({ q: '', loading: false, items: [] });

  // Refs let async callbacks see the current scope/state without re-subscribing.
  const scopeRef = useRef(scopeKey);
  const pagesRef = useRef(pages);
  pagesRef.current = pages;
  const searchAbort = useRef(null);
  const searchTimer = useRef(null);

  // A new team / agent / group set: start clean, drop the old team's cache.
  useEffect(() => {
    scopeRef.current = scopeKey;
    activateTeam(teamId);
    setCore(peekCore(teamId));
    setPages(Object.fromEntries(groupIds.map((g) => [g, emptyPage()])));
    setSearch({ q: '', loading: false, items: [] });
    return () => {
      clearTimeout(searchTimer.current);
      searchAbort.current?.abort();
    };
    // groupIds is derived from scopeKey
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  // First open: the (small, static) core. Cached per team after this.
  useEffect(() => {
    if (!open || !teamId) return undefined;
    const ac = new AbortController();
    loadCore(teamId, { signal: ac.signal })
      .then((data) => { if (!ac.signal.aborted) setCore(data); })
      .catch(() => {});
    return () => ac.abort();
  }, [open, teamId]);

  const loadMore = useCallback((group) => {
    const cur = pagesRef.current[group];
    if (!cur || cur.loading || !cur.hasMore) return;
    const scopeAtStart = scopeRef.current;
    const offset = cur.offset;
    // Mark it in the ref now, not on the next render: two calls in one tick
    // (a scroll event and the short-list check) must not both start a request.
    pagesRef.current = { ...pagesRef.current, [group]: { ...cur, loading: true } };
    setPages((p) => ({ ...p, [group]: { ...p[group], loading: true } }));
    loadGroupPage({ teamId, agentName, group, offset, limit: PAGE_SIZE })
      .then(({ properties, has_more: hasMore }) => {
        if (scopeRef.current !== scopeAtStart) return;
        setPages((p) => {
          const prev = p[group] || emptyPage();
          const seen = new Set(prev.items.map((i) => i.name));
          const fresh = properties.filter((i) => !seen.has(i.name));
          return {
            ...p,
            [group]: {
              items: prev.items.concat(fresh),
              hasMore,
              loaded: true,
              loading: false,
              offset: offset + properties.length,
            },
          };
        });
      })
      .catch((err) => {
        if (scopeRef.current !== scopeAtStart) return;
        // Stop retrying a group that failed; the next open starts fresh.
        setPages((p) => ({ ...p, [group]: { ...(p[group] || emptyPage()), loading: false, hasMore: isAbortError(err) ? (p[group]?.hasMore ?? true) : false, loaded: true } }));
      });
  }, [teamId, agentName]);

  /**
   * Start loading the next page of the first group (in `candidates` order) that
   * has more. One group at a time: nothing starts while another is in flight.
   * @returns {boolean} whether a request was started
   */
  const loadNext = useCallback((candidates) => {
    const cur = pagesRef.current;
    const list = candidates || groupIds;
    if (list.some((g) => cur[g]?.loading)) return false;
    const next = list.find((g) => cur[g] && cur[g].hasMore);
    if (!next) return false;
    loadMore(next);
    return true;
  }, [groupIds, loadMore]);

  const setQuery = useCallback((raw) => {
    const q = String(raw || '').trim();
    clearTimeout(searchTimer.current);
    searchAbort.current?.abort();
    if (!q) {
      setSearch({ q: '', loading: false, items: [] });
      return;
    }
    // Everything loaded already? Then the loaded items are the whole answer.
    const incomplete = () => groupIds.some((g) => !pagesRef.current[g] || pagesRef.current[g].hasMore);
    setSearch((s) => ({ q, loading: incomplete(), items: s.items }));
    searchTimer.current = setTimeout(() => {
      if (!incomplete()) { setSearch({ q, loading: false, items: [] }); return; }
      const ac = new AbortController();
      searchAbort.current = ac;
      const scopeAtStart = scopeRef.current;
      searchGroups({ teamId, agentName, q, groups: groupIds, limit: PAGE_SIZE, signal: ac.signal })
        .then(({ properties }) => {
          if (ac.signal.aborted || scopeRef.current !== scopeAtStart) return;
          setSearch({ q, loading: false, items: properties });
        })
        .catch((err) => {
          if (isAbortError(err) || ac.signal.aborted) return;
          setSearch({ q, loading: false, items: [] });
        });
    }, SEARCH_DEBOUNCE_MS);
  }, [teamId, agentName, groupIds]);

  // Closing the picker abandons any search in flight.
  useEffect(() => {
    if (open) return;
    clearTimeout(searchTimer.current);
    searchAbort.current?.abort();
    setSearch({ q: '', loading: false, items: [] });
  }, [open]);

  return { core, pages, search, loadMore, loadNext, setQuery };
}
